import type { OfficeMachinePair } from '../services/api/doctorOfficesAPI';
import type { UltrasoundMachine } from '../services/api/ultrasoundMachineAPI';
import type { WorklistItem } from '../services/api/worklistAPI';
import type { DoctorOffice } from '../services/api/doctorOfficesAPI';

export const normalizeMachineId = (id?: string) => (id || '').trim().toUpperCase();

export const canEditMachineForStatus = (status?: string): boolean => {
  const normalized = status?.toUpperCase() ?? '';
  return normalized === 'SCHEDULED' || normalized === 'IN_PROGRESS';
};

export type MachineDropdownOption = {
  machine_id: string;
  machine_name: string;
  ae_title?: string;
};

/** Build deduplicated sorted machine list for worklist grid dropdown. */
export function buildMachineDropdownOptions(
  allUltrasoundMachines: UltrasoundMachine[],
  officeMachinePairs: OfficeMachinePair[]
): MachineDropdownOption[] {
  const seen = new Set<string>();
  const options: MachineDropdownOption[] = [];

  const addMachine = (machineId: string, machineName: string, aeTitle?: string) => {
    const key = normalizeMachineId(machineId);
    if (!key || seen.has(key)) return;
    seen.add(key);
    options.push({
      machine_id: machineId,
      machine_name: machineName || machineId,
      ae_title: aeTitle,
    });
  };

  allUltrasoundMachines.forEach((m) => {
    addMachine(m.machine_id, m.machine_name, m.ae_title);
  });

  officeMachinePairs.forEach((p) => {
    addMachine(
      p.machine_id,
      p.machine_details?.machine_name || p.machine_name || p.machine_id,
      p.machine_details?.ae_title || p.ae_title || p.default_ae_title
    );
  });

  return options.sort((a, b) => a.machine_name.localeCompare(b.machine_name, 'es'));
}

export function getEffectiveMachineId(
  item: Pick<WorklistItem, 'ultrasound_machine_id' | 'doctor_office_id'>,
  doctorOffices: DoctorOffice[]
): string | undefined {
  if (item.ultrasound_machine_id) return item.ultrasound_machine_id;
  if (!item.doctor_office_id) return undefined;
  const office = doctorOffices.find((o) => o.office_id === item.doctor_office_id);
  return office?.primary_machine;
}

export function agendaIncludesStatus(status?: string): boolean {
  const itemStatus = status?.toUpperCase();
  // Keep completed rows on Agenda del Día with Completado badge; hide cancelled/no-show.
  return ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED'].includes(itemStatus ?? '');
}
